import escapeHtml from "escape-html";

import BUILDKITE_EMOJI from "~/emoji/buildkite";
import UNICODE_EMOJI from "~/emoji/apple";

type EmojiParseResult =
  | { type: "unicode"; content: string }
  | { type: "image"; content: string; url: string };

type ParseEmojiOptions = {
  escape?: boolean;
};

const EMOJI_CATALOGUES = [BUILDKITE_EMOJI, UNICODE_EMOJI];
const COLON_REGEXP = /:[^\s:]+:(?::skin-tone-[2-6]:)?/g;

// Parse a single emoji shortcode, e.g. ":smile:"
export function parseSimpleEmoji(string: string): EmojiParseResult | null {
  if (!string) {
    return null;
  }

  for (const catalogue of EMOJI_CATALOGUES) {
    if (Object.prototype.hasOwnProperty.call(catalogue.index, string)) {
      const emoji = catalogue.emoji[catalogue.index[string]];
      if (emoji) {
        if (emoji.unicode) {
          return { type: "unicode", content: emoji.unicode };
        }
        if (emoji.image) {
          return {
            type: "image",
            content: emoji.name,
            url: `${catalogue.host}${emoji.image}`,
          };
        }
      }
    }
  }

  // Match unicode shortcode - use Extended_Pictographic to avoid matching
  // ASCII characters like digits (0-9), # and * which are in \p{Emoji}
  // because they can form keycap sequences (e.g., 1️⃣)
  if (/\p{Extended_Pictographic}/u.test(string)) {
    return { type: "unicode", content: string };
  }

  return null;
}

// For HTML string (e.g. "Hello :smile: world!")
export default function parseEmoji(string: string, options: ParseEmojiOptions = {}): string {
  if (!string || string.length === 0) {
    return "";
  }

  // Turn off escaping if the option is explicitly set
  if (options.escape !== false) {
    string = escapeHtml(string);
  }

  return string.replace(COLON_REGEXP, (match) => {
    const parsed = parseSimpleEmoji(match);

    if (parsed) {
      if (parsed.type === "unicode") {
        return `<span class="ui-emoji">${parsed.content}</span>`;
      }
      if (parsed.type === "image") {
        return `<img class="emoji" title="${parsed.content}" alt=":${parsed.content}:" src="${parsed.url}" draggable="false" />`;
      }
    }
    return match;
  });
}
